<template>
  <div>
    <template v-if="isLoadingOverlayShown || isShowPreLoader">
      <client-only>
        <v-lottie position="fixed" />
      </client-only>
    </template>
    <form @submit.prevent="" @keyup="catchEventCodes">
      <template v-if="referralCode">
        <div class="referral-banner-wrapper mb-20">
          <ReferralBanner :referral-name="referralName ? referralName : ''" />
        </div>
      </template>

      <div class="tw-flex tw-flex-col tw-items-start tw-gap-[20px] sm:tw-gap-6 tw-w-full tw-relative tw-z-10">
        <div class="tw-w-full tw-flex tw-flex-col tw-items-start tw-gap-2.5">
          <div class="tw-w-full">
            <div class="general-input">
              <div
                :class="[
                  `field field-text`,
                  {
                    'field-invalid': errors.has('first_name'),
                    'field-filled': loginInfo.first_name.length,
                  },
                ]"
              >
                <input
                  id="first_name"
                  type="text"
                  v-model="loginInfo.first_name"
                  name="first_name"
                  v-validate="'required|min:2'"
                  data-vv-validate-on="blur"
                  @focus="$validator.errors.remove('first_name')"
                  :class="[
                    'input',
                    {
                      invalid: errors.has('first_name'),
                      empty: loginInfo.first_name.length <= 0,
                    },
                  ]"
                  :data-vv-as="$t('placeholders.text.field.first-name')"
                  autocomplete="on"
                  data-testid="first-name-input"
                />
                <label for="first_name" data-tid="first-name-label">
                  {{ $t('placeholders.text.field.first-name') }}
                </label>
              </div>
              <div class="field-error" v-if="errors.first('first_name')">
                <p>
                  {{ errors.first('first_name') }}
                </p>
              </div>
            </div>
          </div>

          <div class="tw-w-full">
            <div class="general-input">
              <div
                :class="[
                  `field field-text`,
                  {
                    'field-filled': loginInfo.last_name.length,
                  },
                ]"
              >
                <input
                  id="last_name"
                  type="text"
                  v-model="loginInfo.last_name"
                  name="last_name"
                  :class="[
                    'input',
                    {
                      empty: loginInfo.last_name.length <= 0,
                    },
                  ]"
                  :data-vv-as="$t('placeholders.text.field.last-name')"
                  data-testid="last-name-input"
                />
                <label for="last_name" data-tid="last-name-label">
                  {{ $t('placeholders.text.field.last-name') }}
                </label>
              </div>
            </div>
          </div>

          <div class="tw-w-full">
            <div class="general-input">
              <div
                :class="[
                  `field field-text`,
                  {
                    'field-invalid': errors.has('email'),
                    'field-filled': loginInfo.email.length,
                  },
                ]"
              >
                <input
                  id="email"
                  type="text"
                  v-model="loginInfo.email"
                  name="email"
                  v-validate="'required|email'"
                  data-vv-validate-on="blur"
                  @focus="$validator.errors.remove('email')"
                  :class="[
                    'input',
                    {
                      invalid: errors.has('email'),
                      empty: loginInfo.email.length <= 0,
                    },
                  ]"
                  :data-vv-as="$t('placeholders.text.field.email')"
                  autocomplete="on"
                  data-testid="email-input"
                />
                <label for="email" data-tid="email-label">
                  {{ $t('placeholders.text.field.email') }}
                </label>
              </div>
              <div class="field-error" v-if="errors.first('email')">
                <p>
                  {{ errors.first('email') }}
                </p>
              </div>
            </div>
          </div>

          <div class="tw-w-full">
            <div class="general-input">
              <div
                :class="[
                  `field field-text`,
                  {
                    'field-invalid': errors.has('password'),
                    'field-filled field-password': loginInfo.password.length,
                  },
                ]"
              >
                <input
                  id="password"
                  :type="passwordFieldType ? 'password' : 'text'"
                  v-model="loginInfo.password"
                  name="password"
                  v-validate="'required|min:8|max:128|verify_password'"
                  data-vv-validate-on="blur"
                  @focus="$validator.errors.remove('password')"
                  :class="[
                    'input',
                    {
                      invalid: errors.has('password'),
                      empty: loginInfo.password.length <= 0,
                    },
                  ]"
                  :data-vv-as="$t('placeholders.text.field.password')"
                  ref="password"
                  data-testid="password-input"
                />
                <label for="password" data-tid="password_label">
                  {{ $t('placeholders.text.field.password') }}
                </label>
                <span
                  v-if="!errors.has('password') || loginInfo.password.length > 0"
                  :class="[
                    'cursor-pointer icon-password',
                    passwordFieldType ? 'airalo-icon-eye-slash' : 'airalo-icon-eye',
                    { 'tw-text-red-600': errors.has('password') },
                  ]"
                  @click="passwordFieldType = !passwordFieldType"
                />
              </div>
              <div class="field-error" v-if="errors.first('password')">
                <p>
                  {{ errors.first('password') }}
                </p>
              </div>
            </div>
          </div>

          <div class="tw-w-full">
            <div class="general-input">
              <div
                :class="[
                  `field field-text`,
                  {
                    'field-invalid': errors.has('password_confirmation'),
                    'field-filled field-password': loginInfo.password_confirmation.length,
                  },
                ]"
              >
                <input
                  id="password_confirmation"
                  :type="confirmPasswordFieldType ? 'password' : 'text'"
                  v-model="loginInfo.password_confirmation"
                  name="password_confirmation"
                  v-validate="'required|confirmed:password'"
                  data-vv-validate-on="blur"
                  @focus="$validator.errors.remove('password_confirmation')"
                  :class="[
                    'input',
                    {
                      invalid: errors.has('password_confirmation'),
                      empty: loginInfo.password_confirmation.length <= 0,
                    },
                  ]"
                  :data-vv-as="$t('placeholders.text.field.password')"
                  data-testid="password-confirmation-input"
                />
                <label for="password_confirmation" data-tid="password_confirmation_label">
                  {{ $t('placeholders.text.field.retype-new-password') }}
                </label>
                <span
                  v-if="!errors.has('password_confirmation') || loginInfo.password_confirmation.length > 0"
                  :class="[
                    'cursor-pointer icon-password',
                    confirmPasswordFieldType ? 'airalo-icon-eye-slash' : 'airalo-icon-eye',
                    { 'tw-text-red-600': errors.has('password_confirmation') },
                  ]"
                  @click="confirmPasswordFieldType = !confirmPasswordFieldType"
                />
              </div>
              <div class="field-error" v-if="errors.first('password_confirmation')">
                <p>
                  {{ errors.first('password_confirmation') }}
                </p>
              </div>
            </div>
          </div>

          <div class="tw-w-full">
            <div class="general-input">
              <div
                :class="[
                  `field field-text`,
                  {
                    'field-filled': loginInfo.voucher.length,
                  },
                ]"
              >
                <input
                  ref="voucherCode"
                  type="text"
                  v-model="loginInfo.voucher"
                  name="voucher"
                  data-vv-validate-on="blur"
                  @focus="$validator.errors.remove('voucher')"
                  @input="loginInfo.voucher = loginInfo.voucher.toUpperCase()"
                  :class="[
                    'input text-uppercase',
                    {
                      empty: loginInfo.voucher.length <= 0,
                    },
                  ]"
                  :data-vv-as="$t('placeholders.text.field.referral-voucher')"
                  data-testid="referral-voucher-input"
                />
                <label for="voucher" data-tid="voucher_label">
                  {{ $t('placeholders.text.field.referral-voucher-optional') }}
                </label>
              </div>
            </div>
          </div>
        </div>

        <Checkmark
          :label="$t('components.register.email-optin')"
          :value="loginInfo.is_newsletter"
          @change="loginInfo.is_newsletter = arguments[0]"
        />

        <div class="tw-w-full sign-up-border-t tw-pt-[20px] sm:tw-pt-6">
          <button
            type="button"
            @click.prevent="validate"
            class="button button-secondary block btn-primary-hv"
            data-testid="signup-button"
          >
            {{ $t('components.register.signup-button') }}
          </button>
        </div>

        <div class="tw-grid tw-grid-cols-2 tw-gap-6 tw-items-start sm:tw-grid-cols-1">
          <div
            class="tw-w-full tw-flex tw-flex-col tw-gap-6 tw-justify-center sm-sign-up-border-t sm:tw-pt-6 sm:tw-order-2"
          >
            <div class="typo-row color-text-secondary color-bg-body color-bg-white-sm tw-text-center">
              {{ $t('components.register.seperator-text') }}
            </div>
            <SocialLogin @onClickSocialLink="onClickSocialLink" />
          </div>
          <i18n
            path="components.register.terms-and-conditions-agreement"
            tag="p"
            class="tw-text-[14px]/[20px] tw-text-secondary tw-mb-0 tw-break-words"
          >
            <template #terms>
              <a
                :href="$utils.replaceTranslationLinks('more-info/terms-conditions')"
                target="_blank"
                class="tw-text-primary tw-underline"
                rel="noopener"
              >
                {{ $t('global.terms-conditions') }}
              </a>
            </template>

            <template #policy>
              <a
                :href="$utils.replaceTranslationLinks('more-info/privacy-policy')"
                target="_blank"
                class="tw-text-primary tw-underline"
                rel="noopener"
              >
                {{ $t('components.sub.navigation.privacy') }}
              </a>
            </template>
          </i18n>
        </div>
      </div>
    </form>

    <client-only>
      <prompt-modal
        :modal-view="isAppleNamePromptShown"
        :overlay-view="false"
        type="double"
        :text="$t('components.social.login.title')"
        :button="{
          text: $t('global.cancel'),
          callbackName: 'closePromptModal',
          isClose: true,
        }"
        :button-second="{
          text: $t('global.continue'),
          callbackName: 'submitNameForApple',
          variant: 'prompt-simple-secondary',
        }"
        @onCloseModal="closePromptModal"
        @onClickButton="submitNameForApple"
      >
        <template>
          <div class="rename-modal-body mt-10">
            <p class="esim-rename-subtitle typo-p color-text-secondary text-center mb-0">
              {{ $t('components.social.login.subtitle') }}
            </p>

            <div class="display-flex flex-column gap-10 mt-20">
              <div class="general-input">
                <div
                  :class="[
                    `field field-text`,
                    {
                      'field-invalid': errors.has('appleFirstName'),
                      'field-filled': appleFirstName.length,
                    },
                  ]"
                >
                  <input
                    id="appleFirstName"
                    type="text"
                    v-model="appleFirstName"
                    name="appleFirstName"
                    v-validate="'required|min:2|max:255'"
                    data-vv-validate-on="blur"
                    @focus="$validator.errors.remove('appleFirstName')"
                    :class="[
                      'input',
                      {
                        invalid: errors.has('appleFirstName'),
                        empty: appleFirstName.length <= 0,
                      },
                    ]"
                    :data-vv-as="$t('placeholders.text.field.first-name')"
                  />
                  <label for="appleFirstName" data-tid="appleFirstName_label">
                    {{ $t('placeholders.text.field.first-name') }}
                  </label>
                </div>
                <div class="field-error" v-if="errors.first('appleFirstName')">
                  <p>
                    {{ errors.first('appleFirstName') }}
                  </p>
                </div>
              </div>

              <div class="general-input">
                <div
                  :class="[
                    `field field-text`,
                    {
                      'field-filled': appleLastName.length,
                    },
                  ]"
                >
                  <input
                    id="apple_last_name"
                    v-model="appleLastName"
                    type="text"
                    name="apple_last_name"
                    :class="[
                      'input',
                      {
                        empty: appleLastName.length <= 0,
                      },
                    ]"
                    :data-vv-as="$t('placeholders.text.field.last-name')"
                  />
                  <label for="appleFirstName" data-tid="appleFirstName_label">
                    {{ $t('placeholders.text.field.last-name') }}
                  </label>
                </div>
                <div class="field-error" v-if="errors.first('appleFirstName')">
                  <p>
                    {{ errors.first('appleFirstName') }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </template>
      </prompt-modal>
    </client-only>
  </div>
</template>

<script>
  import debounce from 'lodash.debounce'
  import cryptoRandomString from 'crypto-random-string'
  import URLJoin from 'url-join'

  import PromptModal from '~/components/modals/prompt-simple.vue'
  import Checkmark from '~/components/UI/Checkmark.vue'
  import { addYears } from 'date-fns'

  export default {
    components: {
      Toggle: () => import('~/components/input/toggle.vue'),
      SocialLogin: () => import('~/components/social/social-login.vue'),
      ReferralBanner: () => import('~/components/banners/referral-auth.vue'),
      Checkmark,
      PromptModal,
    },

    middleware: 'is-auth',

    data() {
      return {
        passwordFieldType: true,

        confirmPasswordFieldType: true,

        loginInfo: {
          first_name: '',
          last_name: '',
          email: '',
          password: '',
          password_confirmation: '',
          is_newsletter: false,
          voucher: this.$route.query.referral || '',
        },

        virtual: {
          doYouHaveVoucherCode: false,
        },

        appleFirstName: '',
        appleLastName: '',
        isAppleNamePromptShown: false,
        appleSignInCredentials: {
          token: null,
          first_name: '',
          last_name: '',
        },

        isLoadingOverlayShown: false,
        isShowPreLoader: false,
      }
    },

    computed: {
      referralCode() {
        return this.$route.query.referral
      },
      referralName() {
        return this.$route.query.referral_name
      },
    },

    head() {
      return {
        title: this.$t('seo.auth.signup.title'),
        meta: [
          {
            hid: 'description',
            name: 'description',
            content: this.$t('seo.auth.signup.description'),
          },
          {
            hid: 'keywords',
            name: 'keywords',
            content: this.$t('seo.auth.signup.keywords'),
          },
          {
            property: 'og:title',
            content: this.$t('seo.auth.signup.title'),
          },
          {
            property: 'og:description',
            content: this.$t('seo.auth.signup.description'),
          },
        ],
      }
    },

    methods: {
      onVoucheCodeToggleChange(val) {
        this.virtual.doYouHaveVoucherCode = val
        if (val) {
          this.$nextTick(() => {
            if (this.$refs.voucherCode) {
              this.$refs.voucherCode.focus()
            }
          })
        }
      },

      catchEventCodes: debounce(function (event) {
        if (event.key === 'Enter' || event.keyCode === 13) {
          this.validate()
        }
      }, 200),

      validate() {
        this.$validator.validateAll().then((result) => {
          if (result) {
            this.save()
          }
        })
      },

      async save() {
        this.isLoadingOverlayShown = true

        window.localStorage.setItem('set_auth_dialogs_email', null)

        if (!this.loginInfo.voucher) {
          this.virtual.doYouHaveVoucherCode = false
        }

        if (this.$route.query.referral) {
          const value = {
            code: this.$route.query.referral,
            via: 'referral_link',
          }

          this.$utils.sendEvent('referralCodeAppliedEvent', value)
        } else if (this?.loginInfo?.voucher) {
          const value = {
            code: this?.loginInfo?.voucher,
            via: 'sign_up',
          }

          this.$utils.sendEvent('referralCodeAppliedEvent', value)
        }

        try {
          const { first_name, last_name, email, password, password_confirmation, is_newsletter, voucher } =
            this.loginInfo

          const registerData = await this.$axios.post(
            '/auth/register',
            {
              first_name,
              last_name,
              email,
              password,
              password_confirmation,
              is_newsletter,
              voucher,
            },
            {
              headers: {
                Accept: 'application/vnd.airalo.security+json;version=2.0',
              },
            }
          )

          this.$utils.sendEvent('signupEvent', { method: 'email' })
          this.$utils.sendEvent('typeClearEvent')

          if (registerData.code === 'auth.verify.required') {
            this.$store.commit('set_auth_dialogs_email', this.loginInfo.email)

            window.localStorage.setItem('set_auth_dialogs_email', this.loginInfo.email)

            window.localStorage.setItem('set_auth_dialogs_pwd', this.loginInfo.password)

            if (localStorage.getItem('referral-reward-shown')) {
              localStorage.removeItem('referral-reward-shown')
            }

            this.isLoadingOverlayShown = false

            this.$router.push(
              this.localePath({
                name: 'verify-index-activation',
                query: {
                  email: this.loginInfo.email,
                },
              })
            )
          } else {
            this.isLoadingOverlayShown = false
          }
        } catch (error) {
          if (error.response.data.code === 'auth.verify.required') {
            this.$store.commit('set_auth_dialogs_email', this.loginInfo.email)

            window.localStorage.setItem('set_auth_dialogs_email', this.loginInfo.email)

            window.localStorage.setItem('set_auth_dialogs_pwd', this.loginInfo.password)

            if (localStorage.getItem('referral-reward-shown')) {
              localStorage.removeItem('referral-reward-shown')
            }

            this.isLoadingOverlayShown = false

            this.$router.push(
              this.localePath({
                name: 'verify-index-activation',
                query: {
                  email: this.loginInfo.email,
                },
              })
            )
          } else {
            this.isLoadingOverlayShown = false

            this.$utils.sendEvent('signupEvent', {
              method: 'form',
              status: 'fail',
              referral_code: this.loginInfo.voucher,
              user_id: null,
            })

            this.$bvToast.toast(error.response.data.message, {
              title: this.$t('global.error.title'),
              variant: 'danger',
              solid: true,
            })
          }
        }
      },

      onClickSocialLink(payload) {
        this.loginWith(payload)
      },

      async loginWith(provider) {
        this.isShowPreLoader = true
        this.$cookiz.set('auth.provider', provider, {
          httpOnly: false,
          expires: addYears(new Date(), 1),
          path: '/',
        })

        const selectedLangFromCookie = this.$cookiz.get('Airalo.locale')

        const aYearFromNow = addYears(new Date(), 1)

        this.$cookiz.set('Airalo.last_locale', selectedLangFromCookie, { path: '/', expires: aYearFromNow })
        this.$cookiz.set('auth.provider', provider, { path: '/', expires: aYearFromNow })

        if (provider === 'apple') {
          this.getAppleCredentials().finally(() => {
            this.isShowPreLoader = false
          })
        } else {
          try {
            await this.$auth.loginWith(provider)

            this.isShowPreLoader = false

            this.$utils.sendEvent('signupEvent', {
              method: provider.toLowerCase(),
            })

            this.$utils.sendEvent('typeClearEvent')
          } catch (error) {
            this.isShowPreLoader = false
          }
        }
      },

      closePromptModal() {
        this.isAppleNamePromptShown = false

        setTimeout(() => {
          this.isShowPreLoader = false
          this.isLoadingOverlayShown = false
        }, 100)
      },

      openPrompModal() {
        this.isAppleNamePromptShown = true
      },

      async getAppleCredentials() {
        try {
          const data = await AppleID.auth.signIn()

          this.appleSignInCredentials.token = data.authorization.id_token

          if (data?.user?.name?.firstName) {
            this.appleSignInCredentials.first_name = data.user.name.firstName
            this.appleFirstName = data.user.name.firstName
          }

          if (data?.user?.name?.lastName) {
            this.appleSignInCredentials.last_name = data.user.name.lastName
            this.appleLastName = data.user.name.lastName
          }

          this.signInWithApple()
        } catch (error) {
          console.log(error)
        }
      },

      async signInWithApple() {
        const referralCode = localStorage.getItem('referral-code')
        let authResponse = null

        this.isLoadingOverlayShown = true

        const socialLoginPayload = {
          token: this.appleSignInCredentials.token,
          first_name: this.appleSignInCredentials.first_name,
          last_name: this.appleSignInCredentials.last_name,
          ...(referralCode && { voucher: referralCode }),
        }

        try {
          authResponse = await this.$axios.post('/v4/auth/social/apple', socialLoginPayload)
        } catch (err) {
          if (err.response.data.code === 'auth.social.missing') {
            if (err.response.data?.user?.token) {
              this.appleSignInCredentials.token = err.response.data.user.token
            }

            this.openPrompModal()
            return
          }
          console.error({ err })
          return
        }

        this.isLoadingOverlayShown = true

        const auth = this.$auth

        if (authResponse) {
          try {
            await auth.setUserToken(authResponse.data.token)
            await auth.setStrategy('local')
            await auth.fetchUser()

            await this.$store.dispatch('userSettings/fetch_settings')

            if (authResponse.is_first_login) {
              this.signInWithApple_performFirstLoginActions(referralCode)
            }

            this.$router.push(this.signInWithApple_getRedirectUrl())
          } catch (err) {
            this.isShowPreLoader = false
            this.isLoadingOverlayShown = false
          }
        }
      },

      signInWithApple_getRedirectUrl() {
        const redirectURL = localStorage.getItem('redirect-url')

        if (redirectURL) {
          localStorage.removeItem('redirect-url')
          return this.localePath(redirectURL)
        } else {
          return this.localePath('/')
        }
      },

      signInWithApple_performFirstLoginActions(referralCode) {
        this.$store.commit('set_is_first_login', true)

        if (referralCode) {
          localStorage.setItem('is-first-login', 'true')
        }

        if (localStorage.getItem('referral-reward-shown')) {
          localStorage.removeItem('referral-reward-shown')
        }

        this.$utils.sendEvent('signupEvent', { method: 'apple' })
        this.$utils.sendEvent('typeClearEvent')
      },

      submitNameForApple() {
        if (!this.appleFirstName) {
          this.$bvToast.toast(this.$t('messages.social.missing.not-filled-error'), {
            title: this.$t('global.error.title'),
            variant: 'danger',
          })

          return
        }

        this.closePromptModal()
        this.appleSignInCredentials.first_name = this.appleFirstName
        this.appleSignInCredentials.last_name = this.appleLastName

        this.isShowPreLoader = true

        this.signInWithApple()
      },

      async changeVirtualVoucherCode() {
        this.virtual.doYouHaveVoucherCode = true

        await this.$nextTick()

        this.$refs.voucherCode.blur()
      },
    },

    watch: {
      'virtual.doYouHaveVoucherCode'(bool) {
        this.onVoucheCodeToggleChange(bool)
      },
    },

    async mounted() {
      const referralCode = this.$route?.query?.referral

      if (referralCode) {
        if (this.$store.getters.is_authenticated) {
          //  After 2023.06.16 (commit 4fd0440, that added 'is-auth' middleware),
          //  this page is not accessible for authenticated users.
          //  So, this block is not necessary anymore and the 'post_user_referral_benefit'
          //  store action will never be called.
          //  Even so, I keep it for now and refactor the function to make it more readable.
          try {
            await this.$store.dispatch('post_user_referral_benefit', referralCode)
            this.$store.commit('set_is_first_login', true)
          } catch (error) {
            setTimeout(() => {
              this.$bvToast.toast(error.response.data.message, {
                title: error.response.data.title,
                variant: 'danger',
                solid: true,
              })
            }, 1000)
          } finally {
            this.$router.push(this.localePath('/'))
          }
        } else {
          localStorage.setItem('referral-code', referralCode)
        }
      } else if (this.$store.getters.is_authenticated) {
        this.$router.push(this.localePath('/'))
      }

      this.$utils.sendEvent('signupStartedEvent')

      window.AppleID.auth.init({
        clientId: process.env.APPLE_CLIENT_ID,
        scope: 'name email',
        redirectURI: URLJoin(process.env.APP_URL, 'auth/callback'),
        state: cryptoRandomString({ length: 256 }),
        usePopup: true,
      })
    },
  }
</script>

<style lang="scss" scoped>
  .signup-agreement {
    word-break: break-word;
    margin: 0 -4px;
  }

  .sign-up-border-t {
    border-top: 1px solid #cfcfd0;
  }

  @media screen and (min-width: 576px) {
    .sm-sign-up-border-t {
      border-top: 1px solid #cfcfd0;
    }
  }
</style>
